import styled from '@emotion/styled'
import { alpha, IconButton } from '@mui/material'
import React from 'react'

import { ImageGallery } from '@f-fertighaus/applications/Next/components/common/ImageGallery/ImageGallery'
import { useImageGalleryContext } from '@f-fertighaus/applications/Next/components/common/ImageGallery/ImageGalleryContext'
import { PinterestIcon } from '@f-fertighaus/applications/Next/components/fertighaus/Icon/PinterestIcon'
import { ImageSource } from '@f-fertighaus/applications/Next/types/houseDetailsResponse'
import { useHouseDetail } from '@f-fertighaus/applications/Next/utils/context/HouseDetail'
import { webtrack } from '@f-fertighaus/applications/Next/utils/webtrack'
import { GalleryImageWrapper } from '@f-fertighaus/applications/Next/views/houseDetailPage/HouseImageGallery/FloorPlansTab'
import { GalleryHouseImagesCtaBanner } from '@f-fertighaus/applications/Next/views/houseDetailPage/HouseImageGallery/GalleryHouseImagesCtaBanner'

export const GalleryHouseImagesTab = () => {
  const {
    state: { exterior_images, interior_images },
    featureFlags
  } = useHouseDetail()
  const { dispatch } = useImageGalleryContext()
  const images = [...exterior_images, ...interior_images]

  const handlePinterestClick = (
    event: React.MouseEvent<HTMLButtonElement>,
    imageSource: ImageSource
  ) => {
    event.stopPropagation()
    window.PinUtils?.pinOne({
      media: imageSource['max-1600x1200.watermarked']
    })
    webtrack({
      category: 'CTA-button',
      action: 'click',
      label: 'pinterest',
      location: 'image-gallery',
      values: [{ type: 'text', value: '(ICON)' }]
    })
  }

  const focusViewImageSources = images.map(imageSource => ({
    src: imageSource['max-1600x1200.watermarked'],
    alt: imageSource.alt_text
  }))

  const handleContentClick = (index: number) => {
    dispatch({
      type: 'openFocusView',
      imageSources: focusViewImageSources,
      displayImgIndex: index
    })
    webtrack({
      category: 'image',
      action: 'click',
      label: 'expand-image',
      location: 'image-gallery',
      values: [{ type: 'text', value: '(ICON)' }]
    })
  }

  const handleExpandIconClick = (
    event: React.MouseEvent<HTMLButtonElement>,
    index: number
  ) => {
    event.stopPropagation()
    dispatch({
      type: 'openFocusView',
      imageSources: focusViewImageSources,
      displayImgIndex: index
    })

    webtrack({
      category: 'CTA-button',
      action: 'click',
      label: 'expand-image',
      location: 'image-gallery',
      values: [{ type: 'text', value: '(ICON)' }]
    })
  }

  const showPinterestButton = !featureFlags.includes('@FF_DISABLE_PINTEREST')

  return (
    <>
      <ImageGallery.ImagesContainer>
        {images.map((imageSource, index) => {
          return (
            <GalleryImageWrapper key={imageSource.alt_text}>
              <ImageGallery.ImageBox
                onContentClick={() => handleContentClick(index)}
                onExpandIconClick={(
                  event: React.MouseEvent<HTMLButtonElement>
                ) => handleExpandIconClick(event, index)}>
                <ImageGallery.Image
                  srcSet={`${imageSource['fill-320x240']} 450w,
                      ${imageSource['width-640x480']} 600w,
                     ${imageSource['width-1280x960']} 1000w,
                     ${imageSource['max-1600x1200.watermarked']} 1600w,
                     ${imageSource.original} 2048w
                     `}
                  sizes='100vw'
                  src={`${imageSource.original}`}
                  alt={imageSource.alt_text}
                />
                {showPinterestButton && (
                  <PinterestButton
                    onClick={(event: React.MouseEvent<HTMLButtonElement>) =>
                      handlePinterestClick(event, imageSource)
                    }>
                    <PinterestIcon />
                  </PinterestButton>
                )}
              </ImageGallery.ImageBox>
            </GalleryImageWrapper>
          )
        })}
      </ImageGallery.ImagesContainer>
      <GalleryHouseImagesCtaBanner />
    </>
  )
}

export const PinterestButton = styled(IconButton)`
  position: absolute;
  top: ${({ theme }) => theme.spacing(2)};
  left: ${({ theme }) => theme.spacing(2)};
  background: ${({ theme }) => alpha(theme.palette.common.black, 0.4)};
  transition: ${({ theme }) =>
    theme.transitions.create(['background-color'], {
      duration: theme.transitions.duration.standard
    })};

  &:hover {
    background: ${({ theme }) => alpha(theme.palette.common.black, 0.6)};
  }
`
